import { api } from '@packages/backend/convex/_generated/api';
import { formatCents, type MenuCvx } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { LogJson } from '@/components/ui+/LogJson';

export const Route = createFileRoute('/r/$restaurantId/menu/overview')({
  component: Component,
});

function Component() {
  const { restaurantId } = Route.useParams();
  const { data, isError, isPending } = useQuery(api.menus.getByRestaurantId, {
    restaurantId,
  });
  const [formattedMenu, setFormattedMenu] = useState<FormattedMenu | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(formattedMenu));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  useEffect(() => {
    if (!data) return;
    setFormattedMenu(transformMenuData(data));
  }, [data]);

  if (isPending) return <LoadingIcon />;
  if (isError) return null;

  if (data == null) return <div>No menu yet.</div>;
  if (formattedMenu == null) return null;

  return (
    <>
      <ul className="list-none space-y-2">
        {Object.entries(formattedMenu).map(([categoryName, category]) => (
          <li key={categoryName} className="space-y-1">
            {/* Category */}
            <div className="bg-neutral-800 p-0.5 px-2 text-white text-md  flex items-center gap-2">
              {categoryName}
              {category.isOnlineOnly && <ColorBadge color="blue">Online only!!</ColorBadge>}
              {category.isPosOnly && <ColorBadge color="yellow">POS only</ColorBadge>}
              {category.isHidden && <ColorBadge color="gray">Hidden</ColorBadge>}
            </div>
            <ul className="pl-4">
              {Object.entries(category.items).map(([itemName, item]) => (
                <li key={itemName} className={'mb-2'}>
                  {/* Menu item */}
                  <div className="p-0.5 pl-2 text-sm bg-neutral-200 dark:bg-neutral-700 flex items-center gap-1">
                    {itemName}
                    {item.price !== null && (
                      <span className="text-muted-foreground text-xs">
                        {formatCents(item.price, '$') === '$0.00'
                          ? '-'
                          : formatCents(item.price, '$')}
                      </span>
                    )}
                    {item.isOnlineOnly && <ColorBadge color="blue">Online only</ColorBadge>}
                    {item.isPosOnly && <ColorBadge color="yellow">POS only</ColorBadge>}
                    {item.isHidden && <ColorBadge color="gray">Hidden</ColorBadge>}
                  </div>
                  <ul className="pl-2">
                    {Object.entries(item.modifierGroups).map(
                      ([groupName, { modifiers, defaultModifierName }]) => (
                        <li key={groupName}>
                          <div className="text-sm font-medium">{groupName}</div>
                          <ul className="pl-4">
                            {Object.entries(modifiers).map(([modifierName, price]) => (
                              <li key={modifierName} className="text-xs">
                                {modifierName === defaultModifierName
                                  ? `${modifierName}* `
                                  : `${modifierName} `}
                                <span className="text-muted-foreground">
                                  {formatCents(price, '$') === '$0.00'
                                    ? ''
                                    : formatCents(price, '$')}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ),
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <SuperAdminOnly>
        <div className={'text-muted-foreground text-sm hover:bg-muted'}>
          View JSON data for this menu
          <ActionButton className="h-6 w-6" onClick={handleCopy} size="icon" variant="ghost">
            {isCopied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-3 w-3" />}
          </ActionButton>
        </div>
        <LogJson jsonData={formattedMenu} requireDebugParam={false} />
      </SuperAdminOnly>
    </>
  );
}

type ModifierGroup = {
  modifiers: Record<string, number>;
  defaultModifierName: string | null;
};

type FormattedMenuItem = {
  price: number | null;
  isOnlineOnly: boolean;
  isPosOnly: boolean;
  isHidden: boolean;
  modifierGroups: Record<string, ModifierGroup>;
};

type FormattedCategory = {
  isOnlineOnly: boolean;
  isPosOnly: boolean;
  isHidden: boolean;
  items: Record<string, FormattedMenuItem>;
};

type FormattedMenu = Record<string, FormattedCategory>;

export const transformMenuData = (menu: MenuCvx): FormattedMenu => {
  // Create a map for categories by UUID for quick lookup (includes visibility)
  const categoryMap = new Map(
    menu.categories.map((category) => [
      category.uuid,
      {
        name: category.name,
        isOnlineOnly: category.isVisibleWeb && !category.isVisiblePos,
        isPosOnly: category.isVisiblePos && !category.isVisibleWeb,
        isHidden: !category.isVisibleWeb && !category.isVisiblePos,
      },
    ]),
  );

  // Create a map for modifiers by UUID for quick lookup of names
  const modifierMap = new Map(menu.modifiers.map((modifier) => [modifier.uuid, modifier.name]));

  // Create a map for modifier groups by UUID and their default modifier
  const modifierGroupMap = new Map(
    menu.modifierGroups.map((group) => [
      group.uuid,
      {
        name: group.name,
        defaultModifierName: group.defaultModifierUuid
          ? modifierMap.get(group.defaultModifierUuid) || null
          : null,
      },
    ]),
  );

  // Create a map for modifiers by group UUID
  const modifiersByGroup = menu.modifiers.reduce((acc, modifier) => {
    if (!acc.has(modifier.modifierGroupUuid)) {
      acc.set(modifier.modifierGroupUuid, []);
    }
    acc.get(modifier.modifierGroupUuid)?.push({
      name: modifier.name,
      price: modifier.price,
    });
    return acc;
  }, new Map<string, Array<{ name: string; price: number }>>());

  // Group menu items by category
  return menu.menuItems.reduce<FormattedMenu>((acc, item) => {
    const category = categoryMap.get(item.categoryUuid);
    const categoryName = category?.name || 'Uncategorized';

    if (!acc[categoryName]) {
      acc[categoryName] = {
        isOnlineOnly: category?.isOnlineOnly ?? false,
        isPosOnly: category?.isPosOnly ?? false,
        isHidden: category?.isHidden ?? false,
        items: {},
      };
    }

    // Get modifiers for this menu item
    const itemModifiers: Record<string, ModifierGroup> = {};

    item.modifierGroupUuids.forEach((groupUuid) => {
      const group = modifierGroupMap.get(groupUuid);
      if (group) {
        const modifiers = modifiersByGroup.get(groupUuid);
        if (modifiers) {
          itemModifiers[group.name] = {
            modifiers: modifiers.reduce<Record<string, number>>((modAcc, mod) => {
              modAcc[mod.name] = mod.price;
              return modAcc;
            }, {}),
            defaultModifierName: group.defaultModifierName,
          };
        }
      }
    });

    acc[categoryName].items[item.name] = {
      price: item.price,
      isOnlineOnly: item.isVisibleWeb && !item.isVisiblePos,
      isPosOnly: item.isVisiblePos && !item.isVisibleWeb,
      isHidden: !item.isVisibleWeb && !item.isVisiblePos,
      modifierGroups: itemModifiers,
    };

    return acc;
  }, {});
};
